import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../lib/format';
import apiClient from '../api/client';
import { useScanInput } from '../hooks/useScanInput';
import { useAudioFeedback } from '../hooks/useAudioFeedback';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { ShelfLocations } from '../components/warehouse/ShelfLocations';

interface PickLine {
  orderLineId: string;
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  manufacturerRef: string | null;
  quantity: number;
  quantityPicked: number;
  status: string;
  /** The part's shelf locations, main first (migration 144). */
  locations: string[];
  /** fn_order_line_location (migration 112) — the same place the order screen and BL show. */
  location: {
    kind: 'SHELF' | 'SUPPLIER' | 'ORDER_BIN';
    label: string | null;
    expectedAt: string | null;
  } | null;
}

interface OrderToPrepare {
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  /** Who sold it — a counter sale's parts go back to that seller (2.3). */
  orderedBy: string | null;
  channel: string;
  /** The bin the parts go into (one per garage, 2.2): known before the first scan when the
   *  garage already has a live bin; null until the first scan opens one. */
  binLocation: string | null;
  openLines: number;
  bucket: string;
}

/**
 * SC-02 step 11 — the picking tablet.
 *
 * Scan-to-validate with Web Audio feedback, because the operator is looking at the bin and not
 * at the screen: 800 Hz says the scan counted, 300 Hz plus a vibration says it did not. The
 * target is a 10-line order in under 5 minutes, which only works if the confirmation is audible.
 *
 * Partial picking is the M7 addition (migration 092 `order_lines.quantity_picked`): scanning one
 * unit of a ten-unit line advances the count instead of closing the line, so ten scans finish it.
 * Before this, a partial scan was refused outright and the operator had to pick blind.
 */
export default function Picking() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const audio = useAudioFeedback();
  const [params, setParams] = useSearchParams();

  const orderId = params.get('orderId') ?? '';
  const [orders, setOrders] = useState<OrderToPrepare[]>([]);
  const queue = orders.filter(
    (o) => o.bucket === 'COUNTER_SALE' || o.bucket === 'READY_TO_PREPARE'
  );
  const current = orders.find((o) => o.orderId === orderId);
  const isCounterSale = current?.channel === 'COUNTER';
  // The bin the first scan opened or joined; until then, the one the dashboard knows.
  const [scanBin, setScanBin] = useState<string | null>(null);
  const bin = scanBin ?? current?.binLocation ?? null;
  const [lines, setLines] = useState<PickLine[]>([]);
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);

  const loadQueue = useCallback(async (): Promise<void> => {
    try {
      const { data } = await apiClient.get<{ ordersToPrepare: OrderToPrepare[] }>(
        '/warehouse/dashboard'
      );
      setOrders(data.ordersToPrepare);
    } catch (err) {
      showApiError(err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLines = useCallback(async (id: string): Promise<void> => {
    try {
      const { data } = await apiClient.get<PickLine[]>(`/warehouse/orders/${id}/pick-list`);
      setLines(data);
    } catch (err) {
      showApiError(err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);
  useEffect(() => {
    setScanBin(null);
    if (!orderId) {
      setLines([]);
      return;
    }
    void loadLines(orderId);
    // A counter sale's units may also be scanned by its seller at the till (option 3): follow
    // the order so the preparer sees a part already taken before walking to the shelf for it.
    const timer = window.setInterval(() => void loadLines(orderId), 5000);
    return () => window.clearInterval(timer);
  }, [orderId, loadLines]);

  const scan = useCallback(
    async (code: string, qty = 1): Promise<void> => {
      if (!orderId || !code.trim()) return;
      setBusy(true);
      try {
        const { data } = await apiClient.post<{
          quantityPicked: number;
          remaining: number;
          linePicked: boolean;
          bin: string | null;
        }>('/warehouse/scan-validate', { orderId, ean: code.trim(), qty });
        audio.success();
        if (data.bin) setScanBin(data.bin);
        push({
          tone: 'success',
          message: data.linePicked
            ? t('picking.linePicked')
            : t('picking.counted', { picked: data.quantityPicked, remaining: data.remaining }),
        });
        setManual('');
        await loadLines(orderId);
      } catch (err) {
        // A wrong product is the mistake that costs a redelivery, so it gets the distinct
        // long buzz + vibration rather than the ordinary error tone. The server names it:
        // SCAN_NOT_IN_ORDER (not a part of this order), SCAN_NOTHING_LEFT (all its units are
        // already in the bin) or SCAN_AMBIGUOUS — all three mean "what you are holding is wrong".
        const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
        if (
          code === 'SCAN_NOT_IN_ORDER' ||
          code === 'SCAN_NOTHING_LEFT' ||
          code === 'SCAN_AMBIGUOUS'
        ) {
          audio.wrongProduct();
        } else audio.error();
        showApiError(err);
      } finally {
        setBusy(false);
      }
    },
    /* The toast / audio / i18n helpers are stable for the lifetime of the screen. Including
       them would rebuild the scan handler on every render, which detaches and reattaches the
       keyboard-wedge listener — mid-scan, on a tablet. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderId, loadLines]
  );

  // Bluetooth HID scanner in keyboard-wedge mode: >6 chars in <100 ms is a scan, not typing.
  useScanInput({
    onScan: (text) => {
      void scan(text);
    },
  });

  const allPicked = lines.length > 0 && lines.every((l) => l.status === 'PICKED');
  // A counter sale hands its in-stock half over today: the supplier half does not hold it back.
  const shelfLines = lines.filter((l) => l.location?.kind !== 'SUPPLIER');
  const shelfPicked = shelfLines.length > 0 && shelfLines.every((l) => l.status === 'PICKED');

  /** The part the list says is on the shelf, and is not. */
  const reportNotFound = async (l: PickLine): Promise<void> => {
    if (!orderId) return;
    const reason = window.prompt(t('picking.notFoundPrompt', { sku: l.sku, name: l.name }));
    if (reason === null) return;
    if (reason.trim().length < 10) {
      audio.error();
      push({ tone: 'warning', message: t('picking.notFoundReasonTooShort') });
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(
        `/orders/${orderId}/lines/${l.orderLineId}/not-found`,
        { reason: reason.trim() },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'warning', message: t('picking.notFoundDone', { sku: l.sku }) });
      await loadLines(orderId);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const markReady = async (): Promise<void> => {
    setBusy(true);
    try {
      await apiClient.post(`/orders/${orderId}/ready`, {});
      push({ tone: 'success', message: t('picking.readyDone') });
      await Promise.all([loadQueue(), loadLines(orderId)]);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={t('picking.title')} description={t('picking.description')} />

      {!orderId ? (
        <div className="overflow-hidden rounded-lg border">
          <ul className="divide-y">
            {queue.map((o) => (
              <li key={o.orderId}>
                <button
                  type="button"
                  onClick={() => setParams({ orderId: o.orderId })}
                  className="flex w-full items-center justify-between px-4 py-4 text-left hover:bg-muted/30"
                >
                  <span>
                    <span className="block font-medium">{o.orderNumber}</span>
                    <span className="block text-xs text-muted-foreground">
                      {o.bucket === 'COUNTER_SALE'
                        ? t('picking.counterSaleBy', { name: o.orderedBy ?? '—' })
                        : (o.customerName ?? '—')}
                    </span>
                  </span>
                  <Badge variant={o.bucket === 'COUNTER_SALE' ? 'destructive' : 'success'}>
                    {t('picking.lines', { count: o.openLines })}
                  </Badge>
                </button>
              </li>
            ))}
            {queue.length === 0 && (
              <li className="px-4 py-8 text-center text-muted-foreground">
                {t('picking.queueEmpty')}
              </li>
            )}
          </ul>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setParams({})}>
              {t('picking.backToQueue')}
            </Button>
            <Link to={`/orders/${orderId}`} className="text-sm underline">
              {t('picking.openOrder')}
            </Link>
            <div className="ml-auto flex items-center gap-2">
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void scan(manual);
                }}
                placeholder={t('picking.scanPlaceholder')}
                className="w-56"
              />
              <Button disabled={busy || !manual.trim()} onClick={() => void scan(manual)}>
                {t('picking.validate')}
              </Button>
            </div>
          </div>

          {isCounterSale && (
            <div className="mb-3 rounded-lg border border-destructive/40 bg-destructive/[.06] px-4 py-3 text-sm font-medium">
              {t('picking.counterSaleBanner', { name: current?.orderedBy ?? '—' })}
            </div>
          )}
          {/* Feedback 1.1 — the preparer is told the customer's bin as soon as the order is
              opened: a scan from here puts the part IN THE BIN (the part's location becomes the
              bin); a scan at the till hands it to the customer. Nothing to choose. */}
          {!isCounterSale && current && (
            <div className="mb-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
              {bin ? t('picking.putInBin', { bin }) : t('picking.binOnFirstScan')}
            </div>
          )}

          <ul className="space-y-2">
            {lines.map((l) => {
              const done = l.status === 'PICKED';
              return (
                <li
                  key={l.orderLineId}
                  className={`flex items-center justify-between gap-3 rounded-lg border p-4 ${done ? 'bg-success/10' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-base font-medium">{l.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {l.sku}
                      {l.ean ? ` · ${l.ean}` : ''}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {l.location?.kind === 'SUPPLIER' ? (
                        // Not received: no shelf to walk to, only when it is due.
                        <Badge variant="warning">
                          {l.location.expectedAt
                            ? t('picking.supplierExpected', {
                                date: formatDate(l.location.expectedAt),
                              })
                            : t('picking.supplierExpectedTbc')}
                        </Badge>
                      ) : l.location?.kind === 'ORDER_BIN' ? (
                        // Received supplier part: it is in the order's bin.
                        <Badge variant="secondary">
                          {t('picking.orderBin', { bin: l.location.label })}
                        </Badge>
                      ) : (
                        // Main location first; "shelf empty" when the picker took the last part
                        // from one of several shelves.
                        <ShelfLocations
                          productId={l.productId}
                          locations={l.locations}
                          canEmpty
                          onChange={(next) =>
                            setLines((prev) =>
                              prev.map((x) =>
                                x.productId === l.productId ? { ...x, locations: next } : x
                              )
                            )
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-2xl font-semibold tabular-nums">
                      {l.quantityPicked}/{l.quantity}
                    </div>
                    {!done && (
                      // Guide E7 — the shelf is empty. The part is NOT put back on sale: it goes
                      // to Zone Q "to be searched", the counter is told to call the customer, and
                      // the rest of the order can still go out.
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1"
                        disabled={busy}
                        onClick={() => void reportNotFound(l)}
                      >
                        {t('picking.notFound')}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
            {lines.length === 0 && (
              <li className="rounded-lg border px-4 py-8 text-center text-muted-foreground">
                {t('picking.nothingToPick')}
              </li>
            )}
          </ul>

          {/* POST /orders/:id/ready is manual and only accepted when ALL lines are PICKED
              (Edge case #5) — the button mirrors that rule rather than discovering it via a 422. */}
          {isCounterSale ? (
            // The seller hands it over at the till (POST /orders/:id/handover readies and
            // delivers it in one go), so there is no READY to mark here — only parts to bring.
            <div
              className={`mt-4 rounded-lg border px-4 py-4 text-center text-base font-medium ${shelfPicked ? 'border-success/40 bg-success/10' : 'text-muted-foreground'}`}
            >
              {shelfPicked
                ? t('picking.bringToCounter', { name: current?.orderedBy ?? '—' })
                : t('picking.markReadyBlocked')}
            </div>
          ) : (
            <Button
              className="mt-4 w-full"
              size="lg"
              disabled={busy || !allPicked}
              onClick={() => void markReady()}
            >
              {allPicked ? t('picking.markReady') : t('picking.markReadyBlocked')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
